import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PageShell } from "@/components/page-shell";
import { ObfuscatedEmail } from "@/components/obfuscated-email";

export const Route = createFileRoute("/journeys")({
  head: () => ({
    meta: [
      { title: "Journeys — Brand Strategy System for Founders | WeVang!" },
      {
        name: "description",
        content:
          "A brand strategy system for founders under real pressure — an active round, a pivot, or growth outpacing your story.",
      },
      { property: "og:title", content: "Journeys — Brand Strategy System for Founders | WeVang!" },
      {
        property: "og:description",
        content:
          "A brand strategy system for founders under real pressure — an active round, a pivot, or growth outpacing your story.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.wevang.com/journeys" },
    ],
    links: [{ rel: "canonical", href: "https://www.wevang.com/journeys" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "WeVang! Journeys",
          serviceType: "Brand strategy consulting",
          description: "A guided system for product-market fit, positioning and the brand that holds it together.",
          url: "https://www.wevang.com/journeys",
          provider: {
            "@type": "Organization",
            name: "WeVang!",
            url: "https://www.wevang.com",
          },
        }),
      },
    ],
  }),
  component: JourneysPage,
});

function Eyebrow({
  children,
  size = "sm",
  align = "left",
}: {
  children: ReactNode;
  size?: "sm" | "lg";
  align?: "left" | "right";
}) {
  const isLarge = size === "lg";
  const isRight = align === "right";
  const line = (
    <span
      className={`h-px w-10 shrink-0 bg-primary ${
        isLarge ? "mt-3" : "mt-[0.55rem]"
      }`}
    />
  );
  const text = (
    <p
      className={`font-bold uppercase tracking-[0.28em] leading-relaxed ${
        isLarge ? "text-[32px] text-foreground" : "text-[14px] text-primary"
      }`}
    >
      {children}
    </p>
  );
  return (
    <div className={`flex items-start gap-4 ${isRight ? "justify-end" : ""}`}>
      {isRight ? text : line}
      {isRight ? line : text}
    </div>
  );
}

function Section({
  children,
  watermark,
  align = "left",
  className = "",
}: {
  children: ReactNode;
  watermark?: string;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <section
      className={`relative isolate overflow-hidden px-6 py-24 md:px-12 md:py-36 ${className}`}
    >
      {watermark && (
        <span
          aria-hidden
          className={`watermark pointer-events-none absolute bottom-4 -z-10 hidden select-none text-[6.5vw] opacity-[0.07] lg:block ${
            align === "right" ? "left-2" : "right-2"
          }`}
        >
          {watermark}
        </span>
      )}
      <div
        className={`relative mx-auto max-w-[1400px] ${
          align === "right" ? "flex justify-end" : ""
        }`}
      >
        <div className="max-w-4xl">{children}</div>
      </div>
    </section>
  );
}

function JourneysPage() {
  return (
    <PageShell>
      {/* PROBLEM > SOLUTION */}
      <Section watermark="Minefield" className="pt-40 md:pt-52">

        <Eyebrow>In a World of Noise...</Eyebrow>
        <h1 className="mt-10 font-display text-[64px] font-extrabold leading-[1.05] tracking-tighter text-foreground">
          You&apos;re not chasing an idea anymore. You&apos;re{" "}
          <span className="font-extrabold text-primary">in it</span> — mid-round, mid-pivot, or
          mid-growth.
        </h1>
        <p className="mt-8 font-sans text-[24px] font-light leading-[1.5] text-foreground/60">
          And the market isn&apos;t waiting.<br />
          The path from <span className="font-semibold text-primary">where you are</span> to{" "}
          <span className="font-semibold text-primary">market leader</span> is a minefield of chaos.
        </p>
      </Section>

      {/* WHAT IS JOURNEYS */}
      <div className="surface-light">
        <Section watermark="System" align="right">
          <div className="text-right">
            <Eyebrow align="right">THIS IS NOT A SERVICE</Eyebrow>
            <h2 className="mt-10 font-display text-[64px] font-extrabold leading-[1.05] tracking-tighter text-foreground">
              We provide<br />
              a proven <span className="font-extrabold text-primary">system for growth</span>.
            </h2>
            <p className="mt-10 font-sans text-[22px] font-normal leading-[1.6] text-foreground/60">
              A proven system that takes you from wherever you&apos;re stuck to market leadership. We
              guide you through every critical inflection — from product-market fit and strategic
              positioning, to{" "}
              <span className="font-semibold text-primary">a brand that owns its category</span> —
              ensuring that at every stage, you&apos;re not just moving forward,{" "}
              <span className="font-semibold text-primary">
                you&apos;re dominating your next challenge
              </span>
              .
            </p>
          </div>
        </Section>
      </div>

      {/* WHY JOURNEYS */}
      <Section watermark="Momentum">
        <Eyebrow>WHY JOURNEYS?</Eyebrow>
        <h2 className="mt-10 font-display text-[48px] font-bold leading-[1.08] tracking-tighter text-foreground">
          We embed our expertise, operating as an extension of your{" "}
          <span className="font-bold text-primary">C-suite</span>.
        </h2>
        <p className="mt-8 font-sans text-[20px] font-normal leading-[1.7] text-foreground/60">
          Journeys isn&apos;t a checklist of deliverables. It&apos;s a system built to move with you —
          from the first inflection to market leadership. We work alongside your team, translating
          strategic direction into tangible outcomes at every critical stage.
        </p>
      </Section>

      {/* WHO IS THIS FOR */}
      <div className="surface-light">
        <Section watermark="Visionaries">
          <Eyebrow>IF YOU&apos;RE READY TO STOP PLAYING THE GAME</Eyebrow>
          <p className="mt-10 font-display text-[36px] font-normal leading-[1.2] tracking-tight text-foreground">
            Journeys is engineered for founders and leaders under real pressure — a round that&apos;s
            live, a pivot that hasn&apos;t landed yet, or growth that&apos;s outpacing your story.
            You&apos;re not short on ambition. You&apos;re short on{" "}
            <span className="font-bold text-primary">
              alignment between what you&apos;ve built and what the market sees
            </span>
            .
          </p>
          <p className="mt-8 font-sans text-[20px] font-normal leading-[1.7] text-foreground/60">
            The path from here to market leadership feels like a minefield: unclear positioning,
            fragmented execution, no proven system to follow. You don&apos;t need another consultant
            handing you a report. You need a system that scales with your ambition — guiding you
            through every critical inflection until{" "}
            <span className="font-bold text-primary">
              domination isn&apos;t just possible, it&apos;s inevitable
            </span>
            .
          </p>
        </Section>
      </div>


      {/* HOW IT WORKS */}
      <section className="relative isolate overflow-hidden px-6 py-24 md:px-12 md:py-36">
        <div className="mx-auto max-w-[1400px]">
          <h2 className="font-display text-[32px] font-bold tracking-tighter text-foreground">
            The path is clear. The system, proven.
          </h2>
          <p className="mt-6 font-sans text-[18px] font-light leading-[1.7] text-foreground/60">
            Three products. One senior team, engineered to move at your moment — whatever it is.
          </p>


          <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                suffix: "Raise",
                message:
                  "Pitch deck, narrative, and visual system — built with Steve, Oli, and Teo in 2–3 weeks, ready before your next investor meeting.",
                price: "Starting at $16,000 USD / €",
              },
              {
                suffix: "Reset",
                message:
                  "A validated ICP, channel, and 90-day roadmap — built with Steve and Oli in 3–4 weeks. A direction, not another deck.",
                price: "Starting at $18,000 USD / €",
              },
              {
                suffix: "Forge",
                message:
                  "Full repositioning: strategy, messaging, identity, and site — built with the whole team over 6–8 weeks.",
                price: "Starting at $28,000 USD / €",
              },
            ].map((p) => (
              <article
                key={p.suffix}
                className="group flex flex-col border border-border bg-white p-7 transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:border-primary hover:shadow-lg"
              >
                <span className="self-start border border-primary/40 bg-primary/8 px-2.5 py-1 font-sans text-[12px] font-bold uppercase tracking-[0.18em] text-primary">
                  Journey
                </span>
                <h3 className="mt-5 font-display text-[24px] font-extrabold uppercase leading-tight tracking-tighter">
                  <span className="text-foreground">We</span>
                  <span className="text-primary">{p.suffix}</span>
                </h3>
                <p className="mt-3 font-sans text-[16px] font-normal leading-relaxed text-foreground/60">
                  {p.message}
                </p>
                <p className="mt-6 font-sans text-[20px] font-bold text-foreground">{p.price}</p>
              </article>
            ))}
          </div>

        </div>
      </section>

      {/* THE OUTCOME */}
      <div className="surface-light">
        <Section watermark="Outcome" align="right">
          <div className="text-right">
            <Eyebrow align="right">THE OUTCOME</Eyebrow>
            <h2 className="mt-10 font-display text-[44px] font-bold leading-[1.1] tracking-tighter text-foreground">
              No disconnected deliverables.<br />
              No generic playbooks.
            </h2>
            <p className="mt-8 font-sans text-[22px] font-normal leading-[1.6] text-foreground/60">
              A system engineered to move with you — ensuring that at every stage, you&apos;re not
              just moving forward,{" "}
              <span className="font-bold text-primary">
                you&apos;re securing your next era of leadership
              </span>
              .
            </p>

          </div>
        </Section>
      </div>

      {/* CTA */}
      <section className="px-6 pb-40 pt-24 text-center md:px-12 md:pb-52">
        <p className="mb-4 font-sans text-sm font-light leading-relaxed text-foreground/70">
          We work with few companies at a time. Book your slot for the next batch.
        </p>
        <ObfuscatedEmail className="text-sm tracking-[0.3em] text-foreground/70 transition-colors hover:text-primary" />
      </section>
    </PageShell>
  );
}
